import type {
  CreateSiteBody,
  CreateSiteResponse,
  DeleteSiteResponse,
  PerimeterConfig,
  PerimetersResponse,
  Site,
  SitesResponse,
  ThreatProfile,
  UpdatePerimeterBody,
  UpdatePerimeterResponse,
  UpdateSiteBody,
  UpdateSiteResponse,
} from '../../shared/types/index.js';
import { attachAppNames, buildAppNameIndex } from '../domain/appNames.js';
import { request } from './client.js';

export async function fetchSites(): Promise<Array<Site>> {
  const { sites } = await request<SitesResponse>('/sites');
  return sites;
}

export async function createSite(payload: CreateSiteBody): Promise<Site> {
  const { site } = await request<CreateSiteResponse>('/sites', { method: 'POST', body: payload });
  return site;
}

export async function updateSite(id: string, payload: UpdateSiteBody): Promise<Site> {
  const { site } = await request<UpdateSiteResponse>(`/sites/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
  return site;
}

export async function deleteSite(id: string): Promise<void> {
  await request<DeleteSiteResponse>(`/sites/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface PerimetersView {
  perimeters: Array<PerimeterConfig>;
  threatProfiles: Array<ThreatProfile>;
}

/** Perimeter sets plus every threat profile, with app bundles resolved to `{ bundle, name }`. */
export async function fetchPerimeters(): Promise<PerimetersView> {
  const { perimeters, threatProfiles, apps } = await request<PerimetersResponse>('/sites/perimeters');
  const names = buildAppNameIndex(apps);
  return {
    perimeters,
    threatProfiles: threatProfiles.map(profile => ({
      ...profile,
      hostileProps: { ...profile.hostileProps, appBundles: attachAppNames(profile.hostileProps.appBundles, names) },
      count: 0,
    })),
  };
}

export async function updatePerimeter(
  id: string,
  body: UpdatePerimeterBody
): Promise<UpdatePerimeterResponse['perimeter']> {
  const { perimeter } = await request<UpdatePerimeterResponse>(`/sites/perimeters/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
  return perimeter;
}
